import React, { useMemo, useState } from 'react';
import { Empty, Select } from 'antd';
import moment from 'moment-timezone';
import { resolveCampaignDateRange } from '../../../../../../core/utils/campaignDateRange';
import { startCase } from 'lodash';

import DetailBar from './components/DetailBar';
import SessionsTable from './components/SessionsTable';
import SessionDeviceBreakdown from './components/SessionDeviceBreakdown';
import PerformanceCardSkeleton from '../../components/PerformanceCardSkeleton';
import EntityCardSkeleton from '../../components/EntityCardSkeleton';
import CampaignRangeSelector from '../../components/CampaignRangeSelector';
import DualAxisAreaChart from '../../../../../shared/designSystem/components/DualAxisAreaChart';
import { MenuItemTextColor } from '../../../../../shared/globalStyling/styledText';
import { useTimezone } from '../../../../../../core/hooks/useTimezone';

const { Option } = Select;

// =============================================================================
// SessionsTabView (V2) — facelifted off legacy Row/Col + BizCharts
// `DualAxisChart` (with its built-in date picker) to the v2 BEM scaffold:
//   • `mjl-ds campaign-sessions-v2` outer wrapper
//   • `<section campaign-overview__performance>` housing the KPI strip
//     (DetailBar) + lifted DateRangePicker + dual-axis trend chart
//   • Device breakdown card below the Performance section
//   • Sessions table at the bottom
//
// Mirrors V1 SessionsTabView's facelift pattern. The picker is lifted out
// of the legacy chart and into the Performance card head as a
// `<CampaignRangeSelector/>`. Its onChange wires straight to the
// controller's `changeDatePicker(dates)` so all filter logic (byDate
// filter + sessions filter + deviceBreakdown recompute) stays in
// `SessionsControllerV2`.
//
// Y-axis dropdowns are inline `<Select/>` controls (matches BasketItems V2
// + V1 Sessions). DualAxisAreaChart's `labelToKey` maps "Sessions" →
// `sessions` and "Impressions" → `impressions`, both already present on
// `formattedByDate` rows from the controller.
// =============================================================================

const TabSkeleton = () => (
  <>
    <PerformanceCardSkeleton />
    <div style={{ marginTop: 10 }}>
      <EntityCardSkeleton />
    </div>
  </>
);

const AXIS_OPTIONS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'impressions', label: 'Impressions' },
];

const SessionsTabView = ({
  campaignOrder,
  summaryData,
  formattedByDate,
  formattedSessions,
  filteredByDateBreakdown,
  sessionsTotal,
  loading,
  sessionsLoading,
  reportDataSummaryLoading,
  changeDatePicker,
  fetchSessionDetails,
  expandedRowsData,
  setExpandedRowsData,
  sessionDetailsLoading,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  document.title = `Sessions - ${campaignOrder.name}`;

  const [leftYLabel, setLeftYLabel] = useState('Sessions');
  const [rightYLabel, setRightYLabel] = useState('Impressions');
  const [byCampaignRangeDate, setByCampaignRangeDate] = useState(null);
  const [rangeType, setRangeType] = useState('Custom');

  // Controller shapes byDate rows with `xAxis: item.date` — but `item.date`
  // can carry a time component (e.g. ISO `2026-05-08T00:00:00Z`), which
  // then leaks into the X-axis tick labels as "00:00:00". Normalize to
  // `'YYYY-MM-DD'` here (same pattern Sessions V1 + Sign Ups V2 use), then
  // sort ascending so the axis reads left → right chronologically.
  const chartRows = useMemo(() => {
    const rows = formattedByDate || [];
    return rows
      .map((row) => ({
        ...row,
        xAxis: moment(row.xAxis).isValid()
          ? moment(row.xAxis).format('YYYY-MM-DD')
          : row.xAxis,
      }))
      .sort((a, b) => moment(a.xAxis).diff(moment(b.xAxis)));
  }, [formattedByDate]);

  // Picker `defaultDateRange` — campaign flight start → today (see
  // core/utils/campaignDateRange). Previously ran to the flight END, which on a
  // live campaign trailed empty months into the future.
  const defaultDateRange = useMemo(() => {
    return resolveCampaignDateRange(
      campaignOrder,
      (chartRows || []).map((row) => row && row.xAxis)
    );
  }, [campaignOrder, chartRows]);

  const handleRangeChange = (v) => {
    setByCampaignRangeDate(v);
    if (v && v.length === 2 && v[0] && v[1]) {
      changeDatePicker(v);
    }
  };

  const sessionsEmpty = !formattedSessions || formattedSessions.length === 0;

  if (reportDataSummaryLoading) return <TabSkeleton />;

  return (
    <div className="mjl-ds campaign-sessions-v2">
      <section className="campaign-overview__performance">
        <header className="campaign-overview__performance-head">
          <div className="campaign-overview__performance-eyebrow mono-eyebrow">
            Performance
          </div>
          {!sessionsEmpty && defaultDateRange && (
            <CampaignRangeSelector
              rangeType={rangeType}
              setRangeType={setRangeType}
              byCampaignRangeDate={byCampaignRangeDate}
              setByCampaignRangeDate={handleRangeChange}
              timezone={effectiveTimezone}
              defaultDateRange={defaultDateRange}
              disabled={sessionsEmpty}
            />
          )}
        </header>

        <DetailBar
          sessions={summaryData?.sessions}
          averageSessionDuration={summaryData?.averageSessionDuration}
          pagesPerSession={summaryData?.pagesPerSession}
          startDate={campaignOrder.startDate}
          endDate={campaignOrder.endDate}
          loading={loading}
        />

        <div className="campaign-overview__chart">
          <div className="campaign-overview__chart-head">
            <div className="campaign-overview__chart-eyebrow mono-eyebrow">
              Trend
            </div>

            <div className="campaign-overview__axis-row">
              <div className="campaign-overview__axis-field">
                <span className="campaign-overview__axis-label mono-eyebrow">
                  <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
                  Left axis
                </span>
                <Select
                  onSelect={(value) => setLeftYLabel(value)}
                  className="campaign-overview__axis-select"
                  value={leftYLabel}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="mjl-ds"
                >
                  {AXIS_OPTIONS.filter((op) => op.label !== rightYLabel).map((option) => (
                    <Option key={option.key} value={option.label}>
                      <MenuItemTextColor text={startCase(option.label)} color={'#7dafff'} />
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
                <span className="campaign-overview__axis-label mono-eyebrow">
                  <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
                  Right axis
                </span>
                <Select
                  onSelect={(value) => setRightYLabel(value)}
                  className="campaign-overview__axis-select"
                  value={rightYLabel}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="mjl-ds"
                >
                  {AXIS_OPTIONS.filter((op) => op.label !== leftYLabel).map((option) => (
                    <Option key={option.key} value={option.label}>
                      <MenuItemTextColor text={startCase(option.label)} color={'#92d5a7'} />
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <div className="campaign-overview__chart-body">
            {sessionsEmpty || !chartRows.length ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No session data yet"
              />
            ) : (
              <DualAxisAreaChart
                dualChartData={chartRows}
                leftY={leftYLabel}
                rightY={rightYLabel}
              />
            )}
          </div>
        </div>
      </section>

      <SessionDeviceBreakdown data={filteredByDateBreakdown || []} />

      <SessionsTable
        data={formattedSessions || []}
        total={sessionsTotal}
        loading={sessionsLoading}
        fetchSessionDetails={fetchSessionDetails}
        expandedRowsData={expandedRowsData}
        setExpandedRowsData={setExpandedRowsData}
        sessionDetailsLoading={sessionDetailsLoading}
      />
    </div>
  );
};

export default SessionsTabView;
